import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, TimerReset, Lock } from "lucide-react";

const GROUPS = [
  {
    title: "New Client & Konsultasi → Kotak Sampah",
    desc: "Belum ada Survei/SPH — kalau tidak ada respon, otomatis diarsipkan ke Kotak Sampah (bisa di-restore, tetap terhitung di total lead), BUKAN Lost.",
    fields: [
      { key: "new_client_trash_workdays", label: "New Client → Tidak Jelas", suffix: "hari kerja tanpa pergerakan" },
      { key: "konsultasi_nudge_workdays", label: "Konsultasi — pengingat follow up di hari ke-", suffix: "hari kerja" },
      { key: "konsultasi_trash_workdays", label: "Konsultasi → Tidak Jelas di hari ke-", suffix: "hari kerja (peringatan otomatis 1 hari sebelumnya)" },
    ],
  },
  {
    title: "SPH → Lost",
    desc: "SPH yang sudah dikirim tanpa respon terlalu lama otomatis dianggap deal mati — ini SATU-SATUNYA jalur otomatis menuju Lost.",
    fields: [
      { key: "sph_nudge_workdays", label: "Pengingat follow up di hari ke-", suffix: "hari kerja" },
      { key: "sph_lost_workdays", label: "Otomatis jadi Lost di hari ke-", suffix: "hari kerja (peringatan otomatis 1 hari sebelumnya)" },
    ],
  },
  {
    title: "Survei — pengingat jadwal & follow-up",
    desc: "Tanggal survei = tanggal Follow-up yang diisi di lead. Pengingat H-3 & H-1 otomatis (tidak perlu diatur). Setelah tanggal survei lewat, sistem terus mengingatkan untuk update status setiap interval di bawah, sampai statusnya benar-benar dipindah (ke SPH/Konsultasi/Lost/Closing).",
    fields: [
      { key: "survei_followup_interval_workdays", label: "Interval pengingat update status setelah tanggal survei lewat", suffix: "hari kerja" },
    ],
  },
  {
    title: "Lost — pengingat soft-follow-up",
    desc: "Lost TIDAK PERNAH pindah status otomatis — ini murni pengingat ke Admin/Owner, siapa tahu masih layak ditawari diskon/follow-up lembut.",
    fields: [
      { key: "lost_softsell_workdays", label: "Ingatkan tiap", suffix: "hari kerja sejak jadi Lost" },
    ],
  },
];

// Pipeline flow, ringkas: New Client & Konsultasi belum punya data valid (belum ada
// Survei/SPH) — kalau tidak ada respon, otomatis ke Kotak Sampah, BUKAN Lost. Lost cuma
// bisa dipindah manual dari Survei/SPH (sudah pernah ada keterlibatan nyata), KECUALI
// SPH yang otomatis jadi Lost kalau tidak direspon terlalu lama. Sekali Lost, tidak
// pernah otomatis pindah ke mana pun — cuma diingatkan untuk soft-follow-up.
export default function PipelineAutomationSettings() {
  const { user } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "chief";
  const qc = useQueryClient();
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["pipeline-automation"],
    queryFn: async () => (await api.get("/settings/pipeline-automation")).data,
  });
  React.useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  const allFields = GROUPS.flatMap((g) => g.fields);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...Object.fromEntries(allFields.map((f) => [f.key, Math.max(1, Number(form[f.key]) || 1)])),
        lost_softsell_enabled: !!form.lost_softsell_enabled,
      };
      const saved = await api.put("/settings/pipeline-automation", payload);
      setForm(saved.data);
      toast.success("Pengaturan otomasi disimpan");
      qc.invalidateQueries({ queryKey: ["pipeline-automation"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="card-surface rounded-2xl p-5 mb-3">
      <h3 className="font-heading font-semibold mb-1 flex items-center gap-2"><TimerReset size={17} />Otomasi Pipeline</h3>
      <p className="text-sm text-zinc-500 mb-1">
        Mengatur kapan lead diingatkan/dipindahkan otomatis di tiap tahap Pipeline.
      </p>
      {!canEdit && (
        <p className="text-xs text-amber-400/80 flex items-center gap-1.5 mb-3"><Lock size={12} />Hanya Owner/Chief yang bisa mengubah — Anda bisa melihat saja.</p>
      )}

      {isLoading || !form ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : (
        <div className="grid gap-4 mt-3">
          {GROUPS.map((g) => (
            <div key={g.title} className="card-surface rounded-xl p-3.5">
              <div className="text-sm font-medium mb-1">{g.title}</div>
              <p className="text-[11px] text-zinc-600 mb-3">{g.desc}</p>
              {/* Grid (bukan flex-wrap) supaya kolom angka semua field rata satu garis
                  vertikal — sebelumnya label panjang bikin baris itu wrap dan inputnya
                  "kelempar" turun sendiri, tidak sejajar dengan input field lain. */}
              <div className="grid grid-cols-[1fr_5.5rem] gap-x-3 gap-y-3 items-center">
                {g.fields.map((f) => (
                  <React.Fragment key={f.key}>
                    <Label className="text-xs text-zinc-400">{f.label}</Label>
                    <Input type="number" min={1} max={90} disabled={!canEdit} value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="bg-[#18181B] border-white/10 h-9 w-full text-center disabled:opacity-60" />
                    <span className="text-[11px] text-zinc-500 -mt-2 col-span-2">{f.suffix}</span>
                  </React.Fragment>
                ))}
                {g.title.startsWith("Lost") && (
                  <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                    <input type="checkbox" disabled={!canEdit} checked={!!form.lost_softsell_enabled}
                      onChange={(e) => setForm({ ...form, lost_softsell_enabled: e.target.checked })}
                      className="accent-blue-600 w-4 h-4 disabled:opacity-60" />
                    Aktifkan pengingat soft-follow-up untuk Lost
                  </label>
                )}
              </div>
            </div>
          ))}
          {canEdit && (
            <Button onClick={save} disabled={saving} className="bg-blue-600 h-10 mt-1 w-fit">
              {saving ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Save size={16} className="mr-1" />}Simpan
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
